import * as React from 'react';
import { useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Check, CheckCircle2, Circle } from 'lucide-react';
import zxcvbn from 'zxcvbn';
import Yup from '../../utils/yupMethods';
import UserService from '../../services/UserService/UserService';
import UserContext from '../../context/UserContext/UserContext';
import ResetTokenStatus from '../../types/ResetTokenStatus';
import Button from '../../components/Button/Button';
import PasswordField from '../../components/PasswordField/PasswordField';
import CustomInput from '../../components/CustomInput/CustomInput';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import AuthLayout from '../../components/AuthLayout/AuthLayout';
import AuthErrorAlert from '../../components/AuthLayout/AuthErrorAlert';
import BackToLoginLink from '../../components/AuthLayout/BackToLoginLink';

interface FormValues {
  password: string;
  confirmPassword: string;
  passwordResetToken: string;
}

const LINK_INVALID_MESSAGE =
  'This link has expired or has already been used. Click the button below to receive a new link.';
const SUPPORT_EMAIL = 'support@japeto.ai';

const HEADING = 'm-0 text-center text-2xl font-extrabold tracking-[-0.5px]';
const SUBTITLE =
  'mx-0 mb-0 mt-2.5 text-center text-sm leading-normal text-auth-muted';
const INLINE_LINK = 'font-bold text-primary underline-offset-4 hover:underline';
const TIP_ROW =
  'flex items-center gap-[9px] text-[13px] font-semibold text-auth-muted';

const STRONG_PASSWORD_MESSAGE = "That's a strong password";

const INVALID_TOKEN_STATUS: ResetTokenStatus = {
  valid: false,
  organisationName: null,
  invitation: false
};

const getPasswordTips = (evaluation: zxcvbn.ZXCVBNResult): readonly string[] =>
  [evaluation.feedback.warning, ...evaluation.feedback.suggestions].filter(
    Boolean
  );

export default function PasswordPage(): React.ReactElement {
  const [isValidatingToken, setIsValidatingToken] =
    React.useState<boolean>(true);
  const [tokenStatus, setTokenStatus] =
    React.useState<ResetTokenStatus>(INVALID_TOKEN_STATUS);
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(
    undefined
  );
  const [resendError, setResendError] = React.useState<string | undefined>(
    undefined
  );
  const [linkSent, setLinkSent] = React.useState<boolean>(false);
  const [isResending, setIsResending] = React.useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);

  const navigate = useNavigate();
  const { setUser } = React.useContext(UserContext);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const passwordResetToken = params.get('resetToken');
  const userName = params.get('userName');

  const validationSchema = Yup.object().shape({
    password: Yup.string().required('Password is required').passwordStrength(),
    confirmPassword: Yup.string()
      .required('Confirm password is required')
      .oneOf([Yup.ref('password'), null], 'Passwords must match')
  });

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    control
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema)
  });

  useEffect(() => {
    setValue('passwordResetToken', passwordResetToken || '');
  }, [setValue, passwordResetToken]);

  const password = useWatch({ control, name: 'password' }) ?? '';
  const passwordEvaluation = React.useMemo(() => zxcvbn(password), [password]);
  const passwordTips = getPasswordTips(passwordEvaluation);

  useEffect(() => {
    if (!passwordResetToken) {
      setIsValidatingToken(false);
      return;
    }
    UserService.validateResetToken(passwordResetToken)
      .then(setTokenStatus)
      .catch(() => setTokenStatus(INVALID_TOKEN_STATUS))
      .finally(() => setIsValidatingToken(false));
  }, [passwordResetToken]);

  const onSubmit = (data: FormValues): void => {
    setIsSubmitting(true);
    UserService.setPassword(data.password, passwordResetToken)
      .then(
        (loginResponse) => {
          setUser({
            ...loginResponse.user,
            ...loginResponse.userAuthStatus
          });
          navigate('/chats');
        },
        (err) => {
          setErrorMessage(
            err.apiError?.message || 'Something went wrong. Please try again.'
          );
        }
      )
      .finally(() => setIsSubmitting(false));
  };

  const onResendLink = (): void => {
    setResendError(undefined);
    setIsResending(true);
    UserService.resetPassword(userName || '')
      .then(() => setLinkSent(true))
      .catch(() => setResendError('We could not send a new link.'))
      .finally(() => setIsResending(false));
  };

  const renderInvalidLink = (): React.ReactElement => (
    <>
      <h1 className={HEADING}>Link no longer valid</h1>
      <p className={SUBTITLE}>
        {linkSent
          ? `If we have an account for ${userName}, a new link is on its way. If you don't receive it shortly, check your spam folder.`
          : LINK_INVALID_MESSAGE}
      </p>
      <AuthErrorAlert message={resendError} />
      {resendError && (
        <p className={SUBTITLE}>
          Please try again, or contact{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} className={INLINE_LINK}>
            {SUPPORT_EMAIL}
          </a>{' '}
          if this keeps happening.
        </p>
      )}
      <Button
        loading={isResending}
        disabled={linkSent}
        icon={linkSent ? <Check className='h-4 w-4' /> : undefined}
        size='xl'
        rounded='xl'
        className='mt-[26px] w-full'
        onClick={onResendLink}
      >
        {linkSent ? 'Link sent' : 'Send me a new link'}
      </Button>
    </>
  );

  const renderSubtitle = (): React.ReactNode => {
    if (tokenStatus.invitation) {
      return (
        <>
          You&apos;ve been invited to join{' '}
          <strong className='text-auth-ink'>
            {tokenStatus.organisationName}
          </strong>{' '}
          on Japeto. Choose a password to finish setting up your account.
        </>
      );
    }
    if (userName) {
      return (
        <>
          Choose a new password for{' '}
          <strong className='text-auth-ink'>{userName}</strong> to get back into
          your account.
        </>
      );
    }
    return 'Choose a new password to get back into your account.';
  };

  const renderPasswordTips = (): React.ReactElement => (
    <div className='flex flex-col gap-[7px] rounded-[10px] bg-auth-wash px-4 py-3.5'>
      {passwordTips.length > 0 ? (
        passwordTips.map((tip) => (
          <div key={tip} className={TIP_ROW}>
            <Circle className='h-4 w-4 shrink-0 text-auth-steel' aria-hidden />
            {tip}
          </div>
        ))
      ) : (
        <div className={TIP_ROW}>
          <CheckCircle2
            className='h-4 w-4 shrink-0 text-brand-secondary'
            aria-hidden
          />
          {STRONG_PASSWORD_MESSAGE}
        </div>
      )}
    </div>
  );

  const renderPasswordForm = (): React.ReactElement => (
    <>
      <h1 className={HEADING}>Set your password</h1>
      <p className={SUBTITLE}>{renderSubtitle()}</p>
      <form
        className='mt-[26px] flex flex-col gap-4'
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <input
          name='username'
          id='username'
          value={userName ?? ''}
          hidden
          readOnly
        />
        <Controller
          name='password'
          control={control}
          render={({ field }) => (
            <PasswordField
              {...field}
              isRegistration
              inputSize='xl'
              error={errors.password}
              fullWidth
            />
          )}
        />
        {renderPasswordTips()}
        <Controller
          name='confirmPassword'
          control={control}
          render={({ field }) => (
            <CustomInput
              {...field}
              type='password'
              label='Confirm password'
              inputSize='xl'
              containerClassName=''
              error={errors.confirmPassword}
              fullWidth
            />
          )}
        />
        <AuthErrorAlert message={errorMessage} />
        <Button
          type='submit'
          loading={isSubmitting}
          size='xl'
          rounded='xl'
          className='mt-1 w-full'
        >
          Set password &amp; log in
        </Button>
      </form>
    </>
  );

  const renderContent = (): React.ReactElement => {
    if (isValidatingToken) {
      return (
        <div className='flex justify-center'>
          <LoadingSpinner size={200} />
        </div>
      );
    }
    return tokenStatus.valid ? renderPasswordForm() : renderInvalidLink();
  };

  return (
    <AuthLayout belowCard={<BackToLoginLink />}>
      <Helmet>
        <title>
          {tokenStatus.valid
            ? 'Japeto - Set Password'
            : 'Japeto - Link Not Valid'}
        </title>
      </Helmet>
      {renderContent()}
    </AuthLayout>
  );
}
